import { ReactElement, useMemo } from 'react'
import { FormControl, MenuItem, Select, Typography, useTheme } from '@mui/material'
import {
  ifcElementTypeLabels,
  type IfcElementType,
} from '@editor/components/Controls/IFCUploadWidget/constants/ifcFilter'
import type { IfcModelMetadata } from '@editor/components/Controls/IFCUploadWidget/types/ifcMetadata'
import {
  getGuidToMGroup,
  upsertElementTypeAssignment,
} from '@editor/components/Controls/IFCUploadWidget/utils/ifcMetadataUtils'
import type { MetadataUpdater } from '@editor/hooks/requests/useUpdateIfcModelMetadata'
import { useIsUpdatingIfcModelMetadata } from '@editorHooks'
import { getContrastTextColor } from 'src/utils/color'

interface Props {
  /** Elements the picked type is assigned to. The control renders nothing when this is empty. */
  guids: string[]
  metadata: IfcModelMetadata | null
  disabled?: boolean
  updateIfcModelMetadata: (updater: MetadataUpdater) => void
  /** Called after a successful assignment (e.g. to clear the scene selection). */
  onAssigned?: () => void
}

/**
 * Type dropdown that assigns the picked IFC element type to `guids` on change (no
 * confirmation). Shows the selection's current type, or a placeholder when it spans multiple types.
 * Shared by the IFC Import type-assignment panel and the IFC element form's "Typ zuweisen" tab.
 */
const IFCTypeAssignmentSelect = ({
  guids,
  metadata,
  disabled,
  updateIfcModelMetadata,
  onAssigned,
}: Props): ReactElement | null => {
  const theme = useTheme()
  const isUpdating = useIsUpdatingIfcModelMetadata()

  const guidToMGroup = useMemo(() => getGuidToMGroup(metadata), [metadata])

  const elementTypeToColor = useMemo(() => {
    const ifcPalette = theme.scenePalette.ifc as Record<string, string>
    return Object.keys(ifcElementTypeLabels).reduce((acc, type) => {
      const color = ifcPalette[type]
      if (color) acc[type] = color
      return acc
    }, {} as Record<string, string>)
  }, [theme.scenePalette.ifc])

  // Effective type shared by all selected elements; empty when the selection spans multiple types.
  const selectedElementType = useMemo<IfcElementType | ''>(() => {
    if (guids.length === 0) return ''
    const types = new Set(guids.map(guid => guidToMGroup[guid]))
    if (types.size !== 1) return ''
    const [only] = Array.from(types)
    return only && only in ifcElementTypeLabels ? (only as IfcElementType) : ''
  }, [guids, guidToMGroup])

  const handleChange = (elementType: IfcElementType) => {
    if (guids.length === 0) return
    updateIfcModelMetadata(current => {
      if (!current) return current
      return {
        ...current,
        elementTypeAssignments: guids.reduce(
          (acc, elementId) => upsertElementTypeAssignment(acc, { elementId, elementType }),
          current.elementTypeAssignments ?? [],
        ),
      }
    })
    onAssigned?.()
  }

  // Placeholder shown when no single type is selected: nothing selected vs. a mixed-type selection.
  const placeholderLabel = guids.length === 0 ? 'Typ auswählen' : 'Verschiedene Typen'

  return (
    <FormControl size="small" variant="outlined" sx={{ minWidth: 180 }} fullWidth>
      <Select
        variant="outlined"
        value={selectedElementType}
        onChange={event => handleChange(event.target.value as IfcElementType)}
        displayEmpty
        disabled={disabled || guids.length === 0 || isUpdating}
        sx={{ '& .MuiSelect-select': { py: 0.25, fontSize: '0.75rem' } }}
        renderValue={value => {
          if (!value) {
            return (
              <Typography component="span" color="text.secondary">
                {placeholderLabel}
              </Typography>
            )
          }
          const label = ifcElementTypeLabels[value as IfcElementType] ?? value
          const bgColor = elementTypeToColor[value as string] ?? theme.palette.grey[300]
          return (
            <Typography
              component="span"
              sx={{
                px: 1,
                py: 0.25,
                borderRadius: 1,
                bgcolor: bgColor,
                color: getContrastTextColor(bgColor),
              }}
            >
              {label}
            </Typography>
          )
        }}
      >
        {Object.entries(ifcElementTypeLabels).map(([value, label]) => {
          const bgColor = elementTypeToColor[value] ?? theme.palette.grey[300]
          const textColor = getContrastTextColor(bgColor)
          const isSelected = value === selectedElementType
          return (
            <MenuItem
              key={value}
              value={value}
              sx={{
                bgcolor: bgColor,
                color: textColor,
                fontWeight: isSelected ? 'bold' : undefined,
                '&.Mui-selected': { bgcolor: bgColor, color: textColor, fontWeight: 'bold' },
              }}
            >
              {label}
            </MenuItem>
          )
        })}
      </Select>
    </FormControl>
  )
}

export default IFCTypeAssignmentSelect
